import { shortDateTime } from '../../shared/dateTime';
import { plural } from '../../shared/plural';
import { shiftDayKey } from './timeline';
import type { FleetGap, FleetRelease } from './vehiclePlanApi';

/**
 * Harmonogram floty według makiety FTL p. Mariusza — Etap 1 (15.10.2026,
 * makieta z 13.10, pkt 9.1; Etap 1 „bez decyzji” — decyzja Bartka
 * 15.10.2026). Przełącznik `fleetScheduleMockup`; reguły wyliczeń są w API
 * (`vehicle-schedules/fleet-mockup.ts`), tu — skala, zapamiętany widok
 * i teksty komórek.
 */

/** Skala osi (makieta: 1, 3, 7, 14 dni, domyślnie 3). */
export const FLEET_SCALES = [1, 3, 7, 14] as const;
export type FleetScale = (typeof FLEET_SCALES)[number];
export const DEFAULT_FLEET_SCALE: FleetScale = 3;

export function isFleetScale(n: unknown): n is FleetScale {
  return typeof n === 'number' && (FLEET_SCALES as readonly number[]).includes(n);
}

/** Podpis przycisku skali. */
export const scaleLabel = (days: FleetScale) => (days === 1 ? '1 dzień' : `${days} dni`);

/**
 * Pierwszy dzień widoku po „dziś” i po zmianie skali — jak w makiecie:
 * przy jednym dniu sam dzisiejszy, przy dłuższej skali od wczoraj (żeby
 * było widać, co się właśnie skończyło).
 */
export function fleetHomeStart(todayKey: string, days: FleetScale): string {
  return days === 1 ? todayKey : shiftDayKey(todayKey, -1);
}

/** Krok strzałek: przy 7 i 14 dniach — tydzień, krócej — dzień (makieta). */
export function fleetStep(days: FleetScale): number {
  return days >= 7 ? 7 : 1;
}

/** Ostatni dzień widoku `RRRR-MM-DD`. */
export function fleetViewTo(fromKey: string, days: FleetScale): string {
  return shiftDayKey(fromKey, days - 1);
}

/**
 * Najmniejsza szerokość dnia (px): przy 14 dniach węższa, żeby oś razem
 * z kolumnami „Wolny od · gdzie” i „Pasujące” zmieściła się przy 1366 px.
 */
export function fleetDayMinWidth(days: FleetScale): number {
  return days >= 14 ? 44 : 56;
}

// ── Zapamiętany widok (makieta pkt 7: „powrót zachowuje filtr”) ─────────

/**
 * „Pokaż”: z alertem, z wariantem (15.10.2026), a z pulpitem planisty
 * (makieta back office v2, A4, 20.10.2026; przełącznik `plannerDashboard`)
 * także wolne teraz, zajęte teraz i bez pozycji — tam prowadzą kafelki
 * „Moje pojazdy”. Wartości to dane API (`vehicle-schedules/fleet-states.ts`).
 */
export type FleetShow = '' | 'alert' | 'variant' | 'freeNow' | 'busyNow' | 'noPosition';
export const FLEET_STATE_SHOW_VALUES = ['freeNow', 'busyNow', 'noPosition'] as const;

const FLEET_SHOW_VALUES: readonly string[] = ['alert', 'variant', ...FLEET_STATE_SHOW_VALUES];

/** Nazwy w „Pokaż”. */
export const FLEET_STATE_SHOW_LABELS: Record<(typeof FLEET_STATE_SHOW_VALUES)[number], string> = {
  freeNow: 'wolne teraz',
  busyNow: 'zajęte teraz',
  noPosition: 'bez pozycji',
};

export function parseFleetShow(v: unknown): FleetShow {
  return typeof v === 'string' && FLEET_SHOW_VALUES.includes(v) ? (v as FleetShow) : '';
}

/** Filtry i skala harmonogramu — wraca się do nich z karty pojazdu. */
export interface FleetViewState {
  days: FleetScale;
  fromKey: string | null;
  search: string;
  companyId: string;
  caretaker: string;
  carType: string;
  busy: '' | 'assigned' | 'free';
  includeArchived: boolean;
  show: FleetShow;
}

/**
 * Klucz w `sessionStorage` — tylko na czas karty przeglądarki: po powrocie
 * z karty pojazdu filtry są te same, a jutro harmonogram startuje od dziś.
 */
export const FLEET_VIEW_KEY = 'marathon.fleetSchedule.view';

export const DEFAULT_FLEET_VIEW: FleetViewState = {
  days: DEFAULT_FLEET_SCALE,
  fromKey: null,
  search: '',
  companyId: '',
  caretaker: '',
  carType: '',
  busy: '',
  includeArchived: false,
  show: '',
};

function sessionStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

const text = (v: unknown) => (typeof v === 'string' ? v : '');

/** Zapamiętany widok; zepsuty zapis albo brak magazynu — wartości domyślne. */
export function readFleetView(storage: Storage | null = sessionStore()): FleetViewState {
  try {
    const raw = storage?.getItem(FLEET_VIEW_KEY);
    if (!raw) return { ...DEFAULT_FLEET_VIEW };
    const v = JSON.parse(raw) as Record<string, unknown>;
    return {
      days: isFleetScale(v.days) ? v.days : DEFAULT_FLEET_SCALE,
      fromKey: typeof v.fromKey === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.fromKey) ? v.fromKey : null,
      search: text(v.search),
      companyId: text(v.companyId),
      caretaker: text(v.caretaker),
      carType: text(v.carType),
      busy: v.busy === 'assigned' || v.busy === 'free' ? v.busy : '',
      includeArchived: v.includeArchived === true,
      show: parseFleetShow(v.show),
    };
  } catch {
    return { ...DEFAULT_FLEET_VIEW };
  }
}

/**
 * Widok z adresu — kafelek pulpitu planisty otwiera harmonogram
 * z `?caretaker=…&show=…` (20.10.2026). Wtedy pozostałe filtry wracają do
 * domyślnych (inaczej zapamiętane „tylko obsadzone” albo szukajka dałyby
 * inną listę niż liczba na pulpicie); skala i początek widoku — jak były.
 * Bez tych parametrów — `null` (zostaje zapamiętany widok).
 */
export function fleetViewFromAddress(
  params: URLSearchParams,
  saved: FleetViewState,
): FleetViewState | null {
  if (!params.has('caretaker') && !params.has('show')) return null;
  return {
    ...DEFAULT_FLEET_VIEW,
    days: saved.days,
    fromKey: saved.fromKey,
    caretaker: params.get('caretaker') ?? '',
    show: parseFleetShow(params.get('show')),
  };
}

export function writeFleetView(state: FleetViewState, storage: Storage | null = sessionStore()): void {
  try {
    storage?.setItem(FLEET_VIEW_KEY, JSON.stringify(state));
  } catch {
    // Magazyn niedostępny (tryb prywatny) — widok działa do odświeżenia.
  }
}

// ── Teksty komórek ───────────────────────────────────────────────────────

/** „Wolny od”: „teraz”, data z godziną albo „—”. */
export function releaseWhen(release: FleetRelease | null | undefined, now: Date = new Date()): string {
  if (!release?.freeFrom) return '—';
  return Date.parse(release.freeFrom) <= now.getTime() ? 'teraz' : shortDateTime(release.freeFrom);
}

/** „Gdzie”: kraj, kod i miejscowość; GPS bez miejscowości — „PL (GPS)”. */
export function releasePlace(release: FleetRelease | null | undefined): string {
  const p = release?.place;
  if (!p) return '—';
  const text = [p.country, p.postalCode, p.city].filter(Boolean).join(' ');
  if (!text) return '—';
  return release?.source === 'gps' && !p.city ? `${text} (GPS)` : text;
}

/** Dymek komórki: skąd czas i miejsce, a przy braku — dlaczego. */
export function releaseTitle(release: FleetRelease | null | undefined, outOfPlanning?: string | null): string {
  if (!release) return outOfPlanning ?? 'Pojazd nie wchodzi teraz w grę.';
  const lines: string[] = [];
  if (release.source === 'order') {
    lines.push(
      `Po rozładunku zlecenia ${release.fromOrder ?? `#${release.fromOrderId ?? '?'}`} (szacowany koniec: przejazd, załadunek i rozładunek).`,
    );
  } else if (release.source === 'gps') {
    lines.push(`Ostatnia pozycja GPS z ${shortDateTime(release.measuredAt)} — pojazd bez dalszych obsadzeń.`);
  }
  if (release.reason) lines.push(release.reason);
  lines.push('Ta sama reguła pozycji co w rekomendacjach.');
  return lines.join('\n');
}

/** Podpis licznika nad osią. */
export const counterLabel = (hours: number) => (hours === 0 ? 'Wolne teraz' : `Wolne za ${hours} h`);

/** Podpis luki na osi. */
export function gapLabel(gap: FleetGap, withMatching: boolean): string {
  if (!withMatching) return 'luka';
  if (gap.matching === undefined || gap.matching === null) return 'luka';
  return `luka · ${plural(gap.matching, 'zlecenie', 'zlecenia', 'zleceń')}`;
}

/** Dymek luki. */
export function gapTitle(gap: FleetGap, withMatching: boolean, horizonDays?: number | null): string {
  const lines = [
    `Luka w planie: od ${shortDateTime(gap.from)}${
      gap.until ? ` do ${shortDateTime(gap.until)} (następne obsadzenie)` : ' — dalej bez obsadzeń'
    }.`,
  ];
  if (withMatching) {
    lines.push(
      gap.matching === null || gap.matching === undefined
        ? `Pasujących zleceń w tej luce nie liczymy: luka zaczyna się za horyzontem rekomendacji${
            horizonDays ? ` (${horizonDays} dni)` : ''
          } albo przeliczenie jeszcze trwa.`
        : `Pasujące zlecenia z załadunkiem w luce: ${gap.matching}. Kliknij, żeby zobaczyć listę.`,
    );
  } else {
    lines.push('Kliknij, żeby otworzyć kartę pojazdu z rekomendowanymi zleceniami.');
  }
  return lines.join('\n');
}
